import { store } from "@/store/store"
import {
	isExpressSetupAtom,
	selectedPhotosAtom,
	updatePhoto,
	updatePhotos,
} from "@/store/photo/photos-state"
import { currentStyleAtom } from "@/store/style/style-state"
import { queueMarkReference } from "@/store/processing/image-processing"
import toast from "react-hot-toast"
import { updateAIStyle } from "@/store/style/update-style"
import { photosViewAtom } from "../photo/photos-view-state"
import { reprocess } from "../processing/mark-reference"
import { processPhotoIfNeeded } from "./apply-style"
import { PhotoRecord } from "@/lib/catalog/types"
import {
	makePhotoKeyNeighborIfNeeded,
	ensureCullingClusterLeaderNotInvalid,
	ensurePhotoNotKeyNeighbor,
	Clusters,
} from "@/lib/ai/ai-cluster"
import { isCalibrationAtom } from "@/store/project/projects-state"
import { resetBiasesInImageInfo } from "../processing/image-info"
import { endCalibration } from "./calibration"
import { lockedSinceSetup, referenceMarkedSinceSetup } from "../app-state"

export function removeFromApproved(id: string, clusters: Clusters | null = null) {
	const isCalibration = store.get(isCalibrationAtom)
	const kind = isCalibration ? "default" : "locked"
	updatePhoto(id, { kind: kind })

	if (kind === "locked") {
		clusters = ensurePhotoNotKeyNeighbor(id, clusters)
		ensureCullingClusterLeaderNotInvalid(id)
	}

	// During setup, reset imageInfo.biases to prevent this photo becoming key photo later while bias is wrong
	// When not in setup, the updateAIStyle() will overwrite new biases anyway when style is truly updated
	if (isCalibration) {
		resetBiasesInImageInfo(id)
	}

	return clusters
}

export function batchLock() {
	const photosToLock = store
		.get(selectedPhotosAtom)
		.filter((p) => p.kind != "locked" && p.kind != "reference")

	if (!photosToLock || photosToLock.length == 0) {
		return
	}

	if (photosToLock.length > 1) {
		toast.loading("Locking photos...", { id: "batchLocking" })
	}

	void updatePhotos((changes) => {
		for (const photo of photosToLock) {
			changes[photo.id] = { kind: "locked" }
		}
	})

	setTimeout(() => {
		;(toast.dismiss("batchLocking"), 500)
	})

	let clusters: Clusters | null = null
	let cullingClusters: Clusters | null = null
	for (const photo of photosToLock) {
		if (photo.kind == "reference") continue
		clusters = ensurePhotoNotKeyNeighbor(photo.id, clusters)
		cullingClusters = ensureCullingClusterLeaderNotInvalid(photo.id, cullingClusters)
	}
}

export function batchUnlock() {
	const photosToUnlock = store.get(selectedPhotosAtom).filter((p) => p.kind === "locked")

	if (!photosToUnlock || photosToUnlock.length == 0) {
		return
	}

	if (photosToUnlock.length > 1) {
		toast.loading("Unlocking photos...", { id: "batchUnlocking" })
	}

	void updatePhotos((changes) => {
		for (const photo of photosToUnlock) {
			changes[photo.id] = { kind: "default" }
		}
	})
	// Use records that reflect the latest state before handing off to processing;
	// selected photo objects can be stale after the store update above.
	const unlockedPhotos = photosToUnlock.map((photo) => ({ ...photo, kind: "default" as const }))

	setTimeout(() => {
		;(toast.dismiss("batchUnlocking"), 500)
	})

	let clusters: Clusters | null = null
	for (const photo of unlockedPhotos) {
		clusters = makePhotoKeyNeighborIfNeeded(photo.id, clusters)
	}

	// safer to process after all changes are done
	for (const photo of unlockedPhotos) {
		void processPhotoIfNeeded(photo, "unlock_photo")
	}
}

export function toggleLock(photo: PhotoRecord) {
	if (photo.kind == "locked") {
		updatePhoto(photo.id, { kind: "default" })
		photo.kind = "default" // the photo passed below need have this update
		makePhotoKeyNeighborIfNeeded(photo.id)
		void processPhotoIfNeeded(photo, "unlock_photo")
	} else if (photo.kind != "reference") {
		updatePhoto(photo.id, { kind: "locked" })
		ensurePhotoNotKeyNeighbor(photo.id)
		ensureCullingClusterLeaderNotInvalid(photo.id)

		store.set(lockedSinceSetup, store.get(lockedSinceSetup) + 1)
	}
}

export function batchMarkReference() {
	const photosToMark = store.get(selectedPhotosAtom).filter((p) => p.kind != "reference")

	if (!photosToMark || photosToMark.length == 0) {
		return
	}

	if (photosToMark.length > 1) {
		toast.loading("Marking photos as references...", {
			id: "batchMarkReference",
		})
	}

	let completeCount = 0
	for (const photo of photosToMark) {
		queueMarkReference(photo, () => {
			completeCount++
			if (completeCount == photosToMark.length) {
				toast.dismiss("batchMarkReference")
				void reprocess(photosToMark, "mark_reference")
			}

			if (store.get(photosViewAtom) === "focus" && store.get(isCalibrationAtom)) {
				setTimeout(() => {
					if (store.get(isExpressSetupAtom)) {
						void endCalibration()
					}
				}, 500)
			}

			if (!store.get(isCalibrationAtom)) {
				store.set(referenceMarkedSinceSetup, true)
			}
		})
	}
}

export async function batchRemoveReference() {
	const photosToUnmark = store.get(selectedPhotosAtom).filter((p) => p.kind === "reference")

	if (!photosToUnmark || photosToUnmark.length == 0) {
		return
	}

	const currentStyle = store.get(currentStyleAtom)
	const needsUpdateStyle = currentStyle && !store.get(isCalibrationAtom)

	if (photosToUnmark.length > 1 && needsUpdateStyle) {
		toast.loading("Removing photos from references...", {
			id: "batchUnmarkReference",
		})
	}

	let clusters: Clusters | null = null
	for (const photo of photosToUnmark) {
		clusters = removeFromApproved(photo.id, clusters)
	}

	if (needsUpdateStyle) {
		await updateAIStyle(currentStyle, "remove_reference")
	}
	toast.dismiss("batchUnmarkReference")
}

export function toggleReference(photo: PhotoRecord, shouldReprocess: boolean) {
	if (photo.kind == "reference") {
		removeFromApproved(photo.id)

		const style = store.get(currentStyleAtom)
		if (style && !store.get(isCalibrationAtom) && shouldReprocess) {
			void updateAIStyle(style, "remove_reference")
		}
	} else {
		queueMarkReference(photo, () => {
			if (shouldReprocess) {
				reprocess([photo], "mark_reference")
			}

			if (store.get(photosViewAtom) === "focus" && store.get(isCalibrationAtom)) {
				setTimeout(() => {
					if (store.get(isExpressSetupAtom)) {
						endCalibration()
					}
				}, 500)
			}

			if (!store.get(isCalibrationAtom)) {
				store.set(referenceMarkedSinceSetup, true)
			}
		})
	}
}
